import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, FileText, User, Home, Shield, AlertCircle } from 'lucide-react';
import { useVerificationStore } from '../stores/verificationStore';
import toast from 'react-hot-toast';

const ReviewSubmit = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  const { submitVerification, uploadStatus, requestId, reset } = useVerificationStore();

  // Build verification items based on actual upload status
  const verificationItems = [
    {
      icon: User,
      title: 'Identity Document',
      status: uploadStatus.idDocument === 'uploaded' ? 'complete' : 'pending',
      description: uploadStatus.idDocument === 'uploaded' ? 'ID uploaded' : 'Not uploaded'
    },
    {
      icon: Shield,
      title: 'Selfie Verification',
      status: uploadStatus.selfie === 'uploaded' ? 'complete' : 'pending',
      description: uploadStatus.selfie === 'uploaded' ? 'Selfie captured' : 'Not captured'
    },
    {
      icon: Home,
      title: 'Address Proof',
      status: uploadStatus.addressProof === 'uploaded' ? 'complete' : 'pending',
      description: uploadStatus.addressProof === 'uploaded' ? 'Document uploaded' : 'Not uploaded'
    },
  ];

  const allComplete = verificationItems.every(item => item.status === 'complete');

  const handleSubmit = async () => {
    if (!consentChecked) {
      toast.error('Please confirm your consent to proceed');
      return;
    }

    if (!requestId) {
      toast.error('Verification session not found. Please start again.');
      navigate('/');
      return;
    }

    if (!allComplete) {
      toast.error('Please complete all verification steps');
      return;
    }

    setIsSubmitting(true);
    try {
      await submitVerification();
      toast.success('Verification submitted successfully!');
      reset(); // Clear the verification store
      navigate('/complete');
    } catch (error) {
      console.error('Failed to submit verification:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 safe-area-top">
        <div className="flex items-center">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 text-gray-600"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-semibold ml-2">Review & Submit</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Ready to submit?
            </h2>
            <p className="text-gray-600">
              Review your information before submitting
            </p>
          </div>

          <div className="space-y-4 mb-8">
            {verificationItems.map((item, index) => {
              const Icon = item.icon;
              const isComplete = item.status === 'complete';
              return (
                <div key={index} className="bg-white rounded-xl p-4 shadow-sm flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isComplete ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <Icon className={`w-6 h-6 ${isComplete ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h3 className="font-medium text-gray-900">{item.title}</h3>
                      {isComplete ? (
                        <Check className="w-4 h-4 text-green-500 ml-2" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-amber-500 ml-2" />
                      )}
                    </div>
                    <p className={`text-sm ${isComplete ? 'text-gray-600' : 'text-amber-600'}`}>{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {!allComplete && (
            <div className="bg-amber-50 rounded-xl p-4 mb-8">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-amber-900">Incomplete verification</h3>
                  <p className="text-sm text-amber-800">Please complete all steps before submitting.</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-blue-50 rounded-xl p-4 mb-8">
            <h3 className="font-medium text-blue-900 mb-2">What happens next?</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Your information will be securely reviewed</li>
              <li>• You'll be notified of the decision</li>
              <li>• Your data is encrypted and protected</li>
            </ul>
          </div>

          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              id="consent"
              required
              className="w-5 h-5 text-blue-600 rounded mt-0.5"
            />
            <label htmlFor="consent" className="text-sm text-gray-600">
              I confirm that the information provided is accurate and I consent to 
              the verification of my identity.
            </label>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 bg-white border-t border-gray-200 safe-area-bottom">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="btn-primary disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Submitting...
            </>
          ) : (
            'Submit Verification'
          )}
        </button>
      </div>
    </div>
  );
};

export default ReviewSubmit;
